<template>
    <div class="bg-white p-3 rounded-lg shadow-sm">
        <div class="float-right mb-4">
            <!-- <span class="status" :class="erp.status">{{ erp.status }}</span> -->
            <button class="btn" @click="showIntroMsg = !showIntroMsg">
                {{ showIntroMsg ? 'Hide' : 'Show' }} intro message
            </button>
        </div>

        <h4 class="mb-3 font-weight-bold">ERP Budgeting</h4>

        <div v-if="parseInt(unclosed) >= 3" class="alert alert-danger">
            You have {{ unclosed }} disbursed ERPs.
            Please close them before creating a new one
        </div>

        <div v-if="erp.code" class="mb-3">
            <h5 class="font-weight-bold">CODE: {{ erp.code }} | USD <span class="font">{{ money(erp.amount) }}</span></h5>
            {{ erp.title }}
        </div>

        <div v-if="erp.is_draft ==1" class="rounded-sm border p-2 border-danger mb-3 font-weight-bold text-danger">
            This is a draft ERP
        </div>

        <Alerts :_success="success" :_error="error"></Alerts>


        <!-- Create new Budget: No ERP selected -->
        <div v-if="showIntroMsg" class="p-3 bg-light border border-secondary rounded-lg mb-3">
            <h6 class="font-weight-bold">Create new ERP budget.</h6>
            Fill in budget items and price per unit.
            <p class="text-muted">
                The total budget for each item will be automatically calculated for you. After submitting your ERP, you
                can enter the actual expenditures for each item, and the system will calculate the variance.<br>
                If the variance is less than zero, a 'top-up' ERP will be generated for additional funding. Conversely,
                if the
                variance is greater than zero, a refund will be initiated.
            </p>
        </div>

        <!-- Budgeting completed -->
        <div v-if="erp.code && erp.budgeted_at && erp.status !=='pending'"
             class="p-2 border border-success text-success font-weight-bold mb-3" style="border-radius: 20px;">
            The budgeting process was completed on {{ formatDate(erp.budgeted_at) }}.
        </div>

        <!-- Budgeting not completed -->
        <div v-if="erp.code && !erp.budgeted_at" class="alert alert-primary mb-3">
            <b>Complete ERP Budgeting</b><br>
            Input the actual value for each item to finalize ERP budgeting. <br>
            A top-up ERP or refund may be generated
            based on the variance. <br>
            <span class="text-muted">Please note that you cannot add or edit list items and their initial values.</span>
            <b>Current Variance: {{ budgeting_currency }} <span class="font">{{ money(grandVariance()) }}</span></b>
        </div>

        <div>
            <div class="border p-3 rounded-lg mb-3">
                <div class="custom-control custom-switch mb-2">
                    <input type="checkbox" class="custom-control-input" id="title-switch" v-model="make_title"
                        @change="setTitle()" :disabled="wait || erp.status !== 'pending'">
                    <label class="custom-control-label" for="title-switch">
                        Utilize the names of list items as the title for the ERP?
                        <p class="text-muted">List items/transactions titles will be used as the title of the ERP</p>
                    </label>
                </div>

                <div class="custom-control custom-switch mb-2">
                    <input type="checkbox" class="custom-control-input" id="copy-switch" v-model="copy_budgeting"
                        @change="copyBudgeting()" :disabled="wait || erp.status !=='pending'">
                    <label class="custom-control-label" for="copy-switch">
                        Copy Budgeting from another ERP?
                        <p class="text-muted">You can copy budgeting transactions from an existing ERP</p>

                        <select v-if="copy_budgeting" class="form-control mb-2" :disabled="!copy_budgeting"
                                v-model="copy_erp"
                                @change="copyErpSet()">
                            <option value="">--select--</option>
                            <option v-for="i in my_requests" :value="i" :key="i">{{ i.title }}</option>
                        </select>
                    </label>
                </div>

                <div class="custom-control custom-switch mb-4">
                    <input type="checkbox" class="custom-control-input" id="misce-switch" v-model="add_misce"
                        @change="misceChange()" :disabled="wait || erp.status !=='pending'">
                    <label class="custom-control-label" for="misce-switch">
                        Add a miscellaneous transaction item with a rate of 5% of the total amount.
                    </label>
                </div>
            </div>

            <div v-if="add_misce" class="mb-3">
                <table class="table table-sm w-50 table-bordered table-top">
                    <tbody>
                    <tr>
                        <td>Total Amount :</td>
                        <td><b>{{ budgeting_currency }} {{ sum('budget') }}</b></td>
                    </tr>
                    <tr>
                        <td>Miscellaneous :</td>
                        <td><b>{{ budgeting_currency }} {{ miscellaneous() }}</b></td>
                    </tr>
                    <tr>
                        <td>Grand Total :</td>
                        <td><b>{{ budgeting_currency }} {{ grandSum('budget') }}</b></td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="erp.status === 'pending'" class="mb-3 bg-light rounded-lg p-3">
                <b>CHANGE CURRENCY TO:</b>&nbsp;
                <select v-model="budgeting_currency" @change="changeCurrency()" class="btn mb-2">
                    <option value="USD">USD</option>
                    <option value="KES">KES</option>
                </select>

                <div class="custom-control custom-switch">
                    <input type="checkbox" class="custom-control-input" id="currency-switch" v-model="update_amounts">
                    <label class="custom-control-label" for="currency-switch">
                        Update amounts when currency changes.
                    </label>
                </div>
            </div>

            <div class="table-responsive">
                <button class="btn">Currency:  {{ budgeting_currency }}</button>
                <table class="table table-sm my-3" style="font-size:12px;">
                    <thead>
                    <tr class="b">
                        <th></th>
                        <th style="width: 36%;">ITEM DESCRIPTION</th>
                        <th style="width: 50px">QTY</th>
                        <th style="width: 140px" class="text-right">UNIT COST</th>
                        <th style="width: 140px" class="text-right">BUDGET</th>

                        <!-- When ERP is already created -->
                        <th style="width: 150px" class="text-right">ACTUAL</th>
                        <th style="width: 150px" class="text-right">VARIANCE</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(i, index) in rows" :key="index">
                        <td class="font-weight-bold">{{ ++index }}</td>
                        <td>
                            <input type="text" class="form-control" placeholder="Item name" @keyup="setTitle()"
                                   @focus="inputFocused(index)" v-model="i.item" :disabled="disableInputs(i)" required>
                        </td>
                        <td>
                            <input type="number" class="form-control text-right" v-model="i.units" min="1" max="100000"
                                   :disabled="disableInputs(i)" required>
                        </td>
                        <td>
                            <input type="number" class="form-control text-right" v-model="i.cost" min="1" max="100000"
                                   :disabled="disableInputs(i)" required>
                        </td>
                        <td class="text-right"><input class="form-control text-right" :value="money(budget(i))" disabled></td>

                        <!-- Actual and variance(computed) only available when ERP is approved -->
                        <td class="text-right">
                            <input type="number" class="form-control text-right" v-model="i.actual" min="1"
                                   max="100000" required :disabled="allowActuals()" @click="actualClicked(i)"
                                   @change="setTitle()">
                        </td>
                        <td class="text-right"><input class="form-control text-right" :value="variance(i)" disabled></td>
                        <td class="text-center">
                            <button class="btn btn-sm py-0 px-2 font-weight-bold" @click="deleteRow(index)"
                                    :disabled="wait || (erp.code && erp.status ==='pending')">x
                            </button>
                        </td>
                    </tr>
                    <tr v-if="add_misce">
                        <td></td>
                        <td><input disabled value="Miscellaneous (5%)" class="form-control"></td>
                        <td><input disabled :value="1" class="form-control text-right"></td>
                        <td><input disabled :value="miscellaneous()" class="form-control text-right"></td>
                        <td class="text-right">{{ miscellaneous() }}</td>
                        <td class="text-right">
                            <input :disabled="allowActuals()" v-model="actual_misce" type="number"
                                   class="form-control text-right" @change="setTitle()">
                        </td>
                        <td class="text-right">{{ miscellaneousVariance() }}</td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr>
                        <td></td>
                        <td></td>
                        <th class="text-right">{{ sum('units') }}</th>
                        <th class="text-right">{{ money(grandSum('cost')) }}</th>
                        <th class="text-right">{{ money(grandSum('budget')) }}</th>

                        <th class="text-right">{{ money(grandSum('actual')) }}</th>
                        <th class="text-right">{{ money(grandVariance()) }}</th>
                        <th></th>
                    </tr>
                    </tfoot>
                </table>
            </div>

            <button v-if="!erp.code || erp.status === 'pending'" class="btn btn-dark" :disabled="wait"
                    @click="addRow()">
                Add Row +
            </button>&nbsp;

            <!--  File upload -->
            <div v-if="!erp.budgeted_at" class="mb-3 mt-3 p-3 border rounded-lg">
                <div class="mb-3 mt-3">
                    <label class="font-weight-bold">File Upload</label>
                    <p>Attach and upload any receipts available for this ERP</p>

                    <div class="custom-file mb-2">
                        <input type="file" class="custom-file-input" ref="fileInput"
                               @change="handleFileSelection"
                               multiple id="customFile"
                               size="10485760"
                               :accept="allowed_files">
                        <!-- Max file input 10MB-->

                        <label class="custom-file-label" for="customFile">Select files to upload</label>
                    </div>
                    <span class="text-muted">Allowed files {{ allowed_files }} |  Max size 10MB</span>

                    <table class="table table-bordered table-sm mt-3">
                        <tr v-for="(file, index) in selectedFilesList" :key="index">
                            <td>{{ file.name }}</td>
                            <td>
                                <a href="#" class="text-danger"
                                   @click="removeFileFromList(index)">
                                    Remove
                                </a>
                            </td>
                        </tr>
                    </table>
                </div>
                <!--  End of file upload -->

                <div v-if="erp.status === 'pending'">
                    <div class="form-group mt-4 mb-3">
                        <label class="font-weight-bold">Select Budget Line</label>
                        <select class="form-control mb-2" v-model="datum.budget_id" :disabled="wait" required>
                            <option value="">--Select--</option>
                            <optgroup v-for="(options, key) in budgets" v-bind:label="key" :key="key">
                                <option v-for="b in options" :key="b.id" :value="b.id">{{ b.name }}</option>
                            </optgroup>
                        </select>
                        <p v-if="!datum.budget_id" class="text-muted">Leave this empty if you do not know the budget</p>
                    </div>

                    <div class="custom-control custom-switch mb-3">
                        <input type="checkbox" class="custom-control-input" id="title-switch" v-model="make_title"
                               @change="setTitle()">
                        <label class="custom-control-label" for="title-switch">
                            Utilize the names of list items as the title for the ERP?
                        </label>
                    </div>

                    <div class="form-group">
                        <label class="font-weight-bold">Title of the ERP <span class="text-danger">*</span></label>
                        <textarea class="form-control mb-2" v-model="datum.title" rows="3" :disabled="wait" required
                                  placeholder="What title would you want to give your ERP?"></textarea>
                        <p v-if="!datum.title" class="text-muted">ERP title is required</p>

                    </div>
                </div>

                <!-- If the overflow is greater than zero, we create a refund, else we create another top up erp-->
                <section v-if="erp && !erp.budgeted_at">
                    <div v-if="grandVariance() < 0">
                        <h4 class="font-weight-bold">Create Top-up Request</h4>
                        <p>There is a deficit of <b>{{ budgeting_currency }} {{ grandVariance() }}</b></p>
                        <div class="form-group mt-1">
                            <label class="font-weight-bold">Top-Up ERP<span class="text-danger">*</span></label>
                            <textarea class="form-control mb-2" v-model="datum.topup" rows="2" :disabled="wait" required
                                      placeholder="Top Up ERP title"></textarea>
                            <p v-if="!datum.topup" class="text-muted">Top up ERP title is required</p>

                        </div>
                    </div>

                    <div v-if="grandVariance() > 0">
                        <h4 class="font-weight-bold">Create Refund</h4>
                        <p class="font-weight-bold">
                            There is an overflow of <b>{{ grandVariance() }}</b>.
                            This will create a refund under the ERP above.
                        </p>
                        <p>
                            All remaining money after expenditure to be remitted back to the bank.
                            Please use the following MPESA Paybill and account :
                        </p>
                        <p class="text-muted">
                            Here is the correct procedure for refunds: <br>
                                Step 1 - Fill in the budget with actual figures <br>
                                Step 2 - Use the company Paybill number <br>
                                Step 3 - Key in the M-Pesa code in the system. <br>
                        </p>
                        <p>
                            Paybill: <b>880100</b> <br>
                            Account No: <b>8137450018</b>
                        </p>

                        <div class="form-group mt-1">
                            <label>Select Payment Method</label>
                            <select v-model="datum.mode" class="form-control" :disabled="wait">
                                <option value="M-Pesa">M-Pesa</option>
                                <option value="Bank">Bank Transfer</option>
                            </select>
                        </div>

                        <div class="form-group mt-1">
                            <label>Enter {{ datum.mode  }} Reference Code<sup class="text-danger">*</sup></label>
                            <input type="text" class="form-control" :placeholder="`Enter ${datum.mode} reference code required`"
                                   v-model="datum.reference">
                        </div>
                    </div>
                </section>

                <button v-if="!erp.budgeted_at" @click="process('no')" class="btn"
                        :disabled="wait || !erp.code && !datum.title">
                    <span v-if="wait">Please wait...</span>
                    <span v-else>Submit</span>
                </button>
                &nbsp;
                <button v-if="erp.status=='pending' && !erp.budgeted_at" class="btn btn-dark" @click="process('yes')"
                        :disabled="wait || erp.is_draft || !erp.code && !datum.title">
                    Save as Draft
                </button>
            </div>
        </div>
    </div>
</template>

<script>

import Alerts from "@/components/Alerts";

export default {
    name: "RequestsBudgeting",
    components: {Alerts},
    props: ['code', 'auth'],
    data() {
        return {
            axios: this.$http,
            base_url: "",

            formData: new FormData(),
            unclosed: 0,

            selectedFiles: [],
            selectedFilesList: [],
            allowed_files: ".xls, .xlsx, .csv, .jpg, .jpeg, .png, .gif, .pdf",

            budgets: [],
            budgeting_currency: 'KES',
            update_amounts: true,

            showIntroMsg: false,
            make_title: true,

            add_misce: false,
            actual_misce: 0,

            copy_budgeting: false,
            copy_erp: {},

            my_requests: [],

            rate: 100,

            datum: {
                title: '',
                topup: '',
                method: '',
                mode: 'M-Pesa',
                budget_id: ''
            },

            erp: {code: null, status: 'pending', documents: []},

            rows: [{
                item: '',
                units: 1,
                cost: 0,
                budget: 0,

                actual: 0,
                variance: 0
            }]
        }
    },
    mounted() {
        if (this.code) {
            this.loadErp()
        }

        this.axios.get(`${this.base_url}/requests/unclosed?check=1`).then(res => {
            this.unclosed = res.data.data
        })

        this.axios.get(`${this.base_url}/budgets`).then(res => {
            this.budgets = res.data.data
        })
    },
    watch: {
        // budgeting_currency() {
        //     this.changeCurrency()
        // }
    },
    computed: {
        getRows() {
            return this.rows
        }
    },
    methods: {
        changeCurrency() {
            if (this.update_amounts) {
                this.rows.forEach(row => {
                    // Convert cost
                    let amount = parseFloat(row.cost);
                    let convertedCost = (this.budgeting_currency === 'USD') ? (amount / this.rate) : (amount * this.rate);
                    row.cost = convertedCost.toFixed(2);

                    // Convert variance
                    let variance = parseFloat(row.variance);
                    let convertedVariance = (this.budgeting_currency === 'USD') ? (variance / this.rate) : (variance * this.rate);
                    row.variance = convertedVariance.toFixed(2);
                });
            }
        },

        loadErp() {
            this.axios.get(`${this.base_url}/request/json/` + this.code).then(res => {
                this.erp = res.data.data
                this.budgeting_currency = this.erp.budgeting_currency

                if (this.erp.budgeting) {
                    this.add_misce = this.erp.miscellaneous
                    this.datum.title = this.erp.title
                    this.datum.budget_id = this.erp.budget_id
                    this.rows = JSON.parse(this.erp.budgeting)
                }
                // else {
                //     this.rows[0].item = this.erp.title.substring(0, 32) + '...'
                //     this.rows[0].cost = this.erp.amount
                // }
            })
        },
        allowActuals() {
            return (this.wait
                || !this.erp.code
                || this.erp.status === 'pending'
                || this.erp.budgeted_at
            )
        },
        disableInputs() {
            return (this.wait || this.erp.status != 'pending')
        },
        setTitle() {
            if (this.make_title) {
                let _title = this.rows.map(a => a.item).toString()

                if (this.add_misce) {
                    _title = _title += ' and Miscellaneous'
                }
                this.datum.title = 'Money to cater for ' + _title
            } else {
                this.datum.title = ''
            }

            if (this.erp) {
                this.datum.topup = `Top-Up: ${this.erp.title}`
            }
            this.computeMiscellanious()
        },
        inputFocused() {
            // Check if there is no one last empty row, add one automatically
            // let last = this.rows[this.rows.length - 1]
            // console.log(last.item)
            // if (last && last.item === '') {
            //     this.addRow()
            // }
        },
        sum(col) {
            let total = this.rows.reduce((sum, row) => {
                return sum += parseFloat(row[col]);
            }, 0);
            return !isNaN(total) ? total : 0
        },
        grandVariance() {
            if (this.add_misce) {
                return (this.sum('variance') + this.miscellaneousVariance())
            }
            return this.sum('variance')
        },
        grandSum(col) {
            if (this.add_misce) {
                return parseFloat(this.sum(col)) + this.miscellaneous()
            }
            return this.sum(col)
        },
        miscellaneous() {
            return parseFloat((this.sum('budget') * 0.05).toFixed(2))
        },
        miscellaneousVariance() {
            let a_misc = parseFloat(this.actual_misce)
            if (isNaN(a_misc)) {
                a_misc = 0
            }
            return (this.miscellaneous() - a_misc)
        },
        budget(row) {
            if (row.cost) {
                row.budget = (parseInt(row.units) * parseFloat(row.cost))
            }
            return row.budget
        },

        variance(row) {
            if (row.actual) {
                row.variance = (row.budget - parseFloat(row.actual))
            }
            return row.variance
        },
        addRow() {
            this.rows.push({
                item: '',
                units: 1,
                cost: 0,
                budget: 0,
                actual: 0,
                variance: 0
            })
            this.setTitle()
        },
        deleteRow(index) {
            this.rows.splice(index, 1);
            this.setTitle()
        },
        actualClicked(row) {
            if (row.actual == 0) {
                row.actual = this.budget(row)
            }
        },
        handleFileSelection(event) {
            this.selectedFiles = event.target.files;
            if (this.selectedFiles) {
                for (let i = 0; i < this.selectedFiles.length; i++) {
                    const file = this.selectedFiles[i];
                    if (!this.selectedFilesList.some(f => f.name === file.name)) {
                        this.selectedFilesList.push(file);
                    }
                }
                this.selectedFiles = null;
                this.$refs.fileInput.value = null;
            }
        },
        removeFileFromList(index) {
            this.selectedFilesList.splice(index, 1);
        },
        misceChange() {
            this.setTitle()
        },
        copyErpSet() {
            if (this.copy_erp) {
                this.rows = JSON.parse(this.copy_erp.budgeting).map(a => {
                    a.actual = 0
                    return a
                })
            } else {
                this.rows = []
                this.addRow()
            }
        },
        copyBudgeting() {
            if (this.copy_budgeting) {
                this.axios.get(`${this.base_url}/requests/mine`).then(res => {
                    this.my_requests = res.data.data
                })
            }
        },
        computeMiscellanious() {
            const misce_index = this.rows.findIndex(row => row.item === 'Miscellaneous');
            if (misce_index !== -1) {
                this.rows[misce_index].cost = (this.sum('budget') * 0.05)
                this.rows[misce_index].budget = (this.sum('budget') * 0.05)
            }
        },

        prepareData(asDraft) {
            let variance = this.grandVariance();
            let type = (variance > 0) ? 'refund' : 'topup';

            this.datum.budget = this.sum('budget')
            this.datum.variance = variance
            this.datum.type = type

            // Add the rows tobe saved as ERP budget
            this.datum.rows = JSON.stringify(this.rows)
            this.datum.code = this.code
            this.datum.currency = this.budgeting_currency
            this.datum.miscellaneous = (this.add_misce == true) ? "yes" : 'no'
            this.datum.draft = asDraft

            for (let i = 0; i < this.selectedFilesList.length; i++) {
                this.formData.append('files[' + i + ']', this.selectedFilesList[i])
            }

            Object.entries(this.datum).forEach(([key, value]) => this.formData.append(key, value));

            this.success = "Loading, please wait..";
        },
        process(asDraft) {
            window.scroll(0, 0)
            this.error = this.success = ''

            // If ERP Status is not pending, user is must feed in actuals
            let actuals = parseFloat(this.grandSum('actual'))
            if (this.erp.status !== 'pending' && actuals === 0) {
                this.error = "Please feed in actual values before you proceed";

            } else {
                this.prepareData(asDraft)
                this.wait = true

                this.axios.post(`${this.base_url}/request/budget`, this.formData, {
                    headers: {'Content-Type': 'multipart/form-data'},
                }).then(res => {
                    console.log(res.data.data)
                    // this.handleSuccess(res.data.data.codes);
                    // localStorage.removeItem('rows')

                }).catch(error => {
                    this.handleError(error);
                }).then(() => {
                    this.wait = false
                })
            }
        },
        handleSuccess(codes) {
            window.scroll(0, 0);
            this.success = 'Saved successfully!';
            if (this.code) {
                this.loadErp()
            } else {
                setTimeout(() => window.location.href = `/requests?codes=${codes}`, 1000);
            }
            setTimeout(() => this.success = '', 1500)
        },

        handleError(error) {
            window.scroll(0, 0);
            this.showError(error);
        }
    }
}
</script>

<style scoped>
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Firefox */
input[type=number] {
    -moz-appearance: textfield;
}

table input, table select {
    width: 100%;
    border: none !important;
    padding: 1px 16px !important;
}

.table-top tr td:first-child {
    text-align: right;
}
</style>
